import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Columns3, X, Loader2, Download, Search } from 'lucide-react';
import {
  ProjectSelectionTable,
  ComparisonSummaryCards,
  ComparisonTable,
  PROJECT_COLORS,
} from '../components/comparison/ComparisonComponents';


function buildCsvRows(config, comparedProjects) {
  const rows = [];
  const headers = ['#', 'Criterion', 'Weight', ...comparedProjects.map(p => p.name), 'Best'];
  rows.push(headers);

  config.phase1.forEach((c, i) => {
    const scores = comparedProjects.map(p => {
      const pd = p.phase1;
      if (!pd) return '--';
      const s = pd.scores.find(s => s.criteria_id === c.id);
      return s ? s.score : '--';
    });
    const valid = scores.filter(s => typeof s === 'number');
    const best = valid.length > 0 ? Math.max(...valid) : '--';
    rows.push([i + 1, `"${c.name}"`, c.weight, ...scores, best]);
  });

  const totals = comparedProjects.map(p => {
    const pd = p.phase1;
    return pd ? `${pd.total_score}/${pd.max_score} (${Math.round((pd.total_score / pd.max_score) * 100)}%)` : '--';
  });
  rows.push(['', 'TOTAL', '', ...totals, '']);

  if (comparedProjects.some(p => p.phase2)) {
    rows.push([]);
    rows.push(['', 'PHASE 2', '', ...comparedProjects.map(() => ''), '']);
    rows.push(['#', 'Criterion', '', ...comparedProjects.map(p => p.name), 'Best']);
    config.phase2.forEach((c, i) => {
      const scores = comparedProjects.map(p => {
        if (!p.phase2) return '--';
        const s = p.phase2.scores.find(s => s.criteria_id === c.id);
        return s ? s.score : '--';
      });
      const valid = scores.filter(s => typeof s === 'number');
      rows.push([i + 1, `"${c.name}"`, '', ...scores, valid.length > 0 ? Math.max(...valid) : '--']);
    });
    const p2Totals = comparedProjects.map(p => {
      const pd = p.phase2;
      return pd ? `${pd.total_score}/${pd.max_score} (${Math.round((pd.total_score / pd.max_score) * 100)}%)` : '--';
    });
    rows.push(['', 'TOTAL', '', ...p2Totals, '']);
  }
  return rows;
}


export default function ComparisonPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState({});
  const [comparedProjects, setComparedProjects] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    api.getScorecardConfig()
      .then(res => setConfig(res.data))
      .catch(() => toast.error('Failed to load scorecard config'))
      .finally(() => setLoading(false));
  }, []);

  // Search-as-you-type (debounced). Empty query shows screened (comparable) deals by default.
  useEffect(() => {
    setSearching(true);
    const timer = setTimeout(() => {
      api.compareSearch(searchQuery.trim())
        .then(res => setSearchResults(res.data))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleProject = (id, project) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length < 4) return [...prev, id];
      toast.error('You can compare up to 4 deals');
      return prev;
    });
    if (project) setSelectedMeta(prev => ({ ...prev, [id]: project }));
  };

  const handleToggle = (id) => {
    const proj = searchResults.find(p => p.id === id);
    toggleProject(id, proj);
  };

  const runComparison = async () => {
    if (selectedIds.length < 2) { toast.error('Select at least 2 projects'); return; }
    setComparing(true);
    try {
      const res = await api.compareProjects(selectedIds);
      setComparedProjects(selectedIds.map(id => res.data.find(p => p.id === id)).filter(Boolean));
    } catch (err) { toast.error(err.response?.data?.detail || 'Comparison failed'); }
    finally { setComparing(false); }
  };

  const exportToCsv = () => {
    if (!comparedProjects || !config) return;
    const rows = buildCsvRows(config, comparedProjects);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `olta-comparison-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Comparison exported as CSV');
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="comparison-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">Deal Comparison</h1>
        <p className="text-muted-foreground text-sm mt-1">Side-by-side scorecard analysis for shortlisting</p>
      </div>

      {!comparedProjects ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Search and select <span className="font-mono text-foreground">2-4</span> deals to compare</p>
            <Button data-testid="run-comparison-button" onClick={runComparison} disabled={selectedIds.length < 2 || comparing}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-sm h-9 px-4">
              {comparing
                ? <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...</span>
                : <span className="flex items-center gap-2"><Columns3 className="w-3.5 h-3.5" /> Compare ({selectedIds.length})</span>}
            </Button>
          </div>

          {/* Search box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="comparison-search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search deals by name..."
              className="pl-10 bg-secondary border-border text-foreground rounded-sm h-10 text-sm" />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />}
          </div>

          {/* Selected chips — persist across searches */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="comparison-selected-chips">
              <span className="text-xs text-muted-foreground">Selected:</span>
              {selectedIds.map((id, i) => (
                <div key={id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${PROJECT_COLORS[i].border} ${PROJECT_COLORS[i].bg}`}>
                  <span className={`text-xs font-medium ${PROJECT_COLORS[i].text}`}>{selectedMeta[id]?.name || id}</span>
                  <button data-testid={`remove-selected-${i}`} onClick={() => toggleProject(id)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && !searching ? (
            <div className="border border-border rounded-sm bg-card p-12 text-center" data-testid="no-analyzed-projects">
              <Columns3 className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground text-sm">{searchQuery.trim() ? 'No deals match your search' : 'No screened deals yet'}</p>
              <p className="text-muted-foreground/60 text-xs mt-1">{searchQuery.trim() ? 'Try a different name' : 'Screen deals first, or search any deal by name'}</p>
            </div>
          ) : (
            <ProjectSelectionTable projects={searchResults} selectedIds={selectedIds} onToggle={handleToggle} />
          )}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {comparedProjects.map((p, i) => (
                <div key={p.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border ${PROJECT_COLORS[i].border} ${PROJECT_COLORS[i].bg}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: PROJECT_COLORS[i].bar }} />
                  <span className={`text-sm font-medium ${PROJECT_COLORS[i].text}`}>{p.name}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="export-comparison-csv" onClick={exportToCsv} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-3">
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
              <Button data-testid="clear-comparison-button" onClick={() => { setComparedProjects(null); setSelectedIds([]); }} className="bg-secondary hover:bg-muted text-secondary-foreground rounded-sm font-medium text-xs h-8 px-3">
                <X className="w-3.5 h-3.5 mr-1" /> New Comparison
              </Button>
            </div>
          </div>

          <ComparisonSummaryCards projects={comparedProjects} />

          <Tabs defaultValue="phase1" className="w-full">
            <TabsList className="bg-secondary border border-border rounded-sm h-9 p-0.5" data-testid="comparison-phase-tabs">
              <TabsTrigger value="phase1" data-testid="comparison-phase1-tab" className="rounded-sm h-8 text-xs font-medium data-[state=active]:bg-card data-[state=active]:text-foreground px-4">Phase 1 — K.O. Screen</TabsTrigger>
              {comparedProjects.some(p => p.phase2) && (
                <TabsTrigger value="phase2" data-testid="comparison-phase2-tab" className="rounded-sm h-8 text-xs font-medium data-[state=active]:bg-card data-[state=active]:text-foreground px-4">Phase 2 — Deep Dive</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="phase1" className="mt-4">
              {config && <ComparisonTable phase="phase1" criteriaList={config.phase1} projects={comparedProjects} />}
            </TabsContent>
            {comparedProjects.some(p => p.phase2) && (
              <TabsContent value="phase2" className="mt-4">
                {config && <ComparisonTable phase="phase2" criteriaList={config.phase2} projects={comparedProjects} />}
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
}
